/**
 * The SHAPE of the document tree, as pure functions over a `listTree()` array.
 *
 * Two rules the app already wrote down in prose, extracted here so the sidebar
 * and MCP share ONE implementation of each rather than two that can drift:
 *
 * 1. **Deleting any node deletes its whole subtree.** `DocumentsSidebar.js`
 *    states it verbatim: *"documents can hold subpages now, so a document
 *    delete must cascade exactly like a folder delete or it orphans the
 *    children (rows still in the store, unreachable from the root)"*. An
 *    orphaned child is not a cosmetic problem — the row keeps syncing, keeps
 *    counting against the per-scope row cap, and can never be reached or
 *    deleted again from any UI.
 *
 * 2. **A node may not be moved into its own descendant.** Same file, on the
 *    drag path: *"a document dropped into its own descendant would detach the
 *    whole subtree from the root — unreachable, invisible in the sidebar"*.
 *    That is the same failure arriving through a different door.
 *
 * ## Cycle safety, and why it is here rather than at the call sites
 *
 * A stored tree is not guaranteed acyclic: it is a set of independently synced
 * rows, each carrying its own `parentId`, and two devices can write a pair of
 * reparents that together form a loop. The sidebar's own walk had no visited
 * set, so such a tree would hang the renderer's delete path; `reveal.js`'s
 * `ancestorChain` already carries the guard and says so. Both walks here are
 * bounded by the node count, in every direction.
 *
 * Nothing in this file touches the DOM, a store, `invoke` or `Date.now()`.
 */

/**
 * The bucket a top-level node (`parentId == null`) lives in.
 *
 * A **Symbol**, not the string `'root'`, and that is not decoration: the
 * sidebar's own index used the literal string, so a node whose id happened to
 * be `"root"` shared a bucket with every top-level node — its "subtree" then
 * silently swallowed the whole tree, which for a delete means deleting the
 * whole tree. Ids are `crypto.randomUUID()` today so it is not reachable in the
 * app, but a delete path is the wrong place to rely on that, and a Symbol
 * cannot collide with any id by construction. (It was reached immediately by a
 * test fixture, which is how it was found.)
 */
export const ROOT = Symbol('documents.root');

const bucketOf = (parentId) => (parentId == null || parentId === ROOT ? ROOT : String(parentId));

/**
 * `{ nodeById, childrenByParent }` for one `listTree()` array. Top-level
 * children live under `ROOT`; use `childrenOf(index, null)` rather than
 * reaching into the map.
 *
 * `compare` is optional and, when given, orders each sibling group — the
 * sidebar passes its own comparator so the extraction is behaviour-preserving
 * for the render path, and MCP passes none where order does not matter.
 */
export function indexNodes(nodes, compare) {
  const nodeById = new Map();
  const childrenByParent = new Map();
  for (const node of nodes || []) {
    nodeById.set(String(node.id), node);
    const key = bucketOf(node.parentId);
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key).push(node);
  }
  if (compare) for (const arr of childrenByParent.values()) arr.sort(compare);
  return { nodeById, childrenByParent };
}

/** Accept either a raw `listTree()` array or an `indexNodes()` result. */
function asIndex(nodesOrIndex) {
  return Array.isArray(nodesOrIndex) ? indexNodes(nodesOrIndex) : nodesOrIndex;
}

/** The direct children of `parentId` — `null` (or `ROOT`) for the section root. */
export function childrenOf(nodesOrIndex, parentId) {
  const { childrenByParent } = asIndex(nodesOrIndex);
  return childrenByParent.get(bucketOf(parentId)) || [];
}

/**
 * `rootId` and every node beneath it, **root first, then descendants**
 * (pre-order DFS). Returns `[]` when `rootId` names nothing.
 *
 * The order is load-bearing in both directions and callers must pick one
 * deliberately:
 *
 * - the sidebar deletes in THIS order, which is what it has always done;
 * - a delete that wants children removed before their parent — so a mid-way
 *   failure leaves an over-full parent (visible, fixable) rather than orphans
 *   under a deleted parent (invisible, unreachable) — reverses it. Reversing a
 *   pre-order DFS is exactly "every node after all of its descendants",
 *   because a parent always precedes its descendants in pre-order.
 */
export function collectSubtree(nodesOrIndex, rootId) {
  const index = asIndex(nodesOrIndex);
  const out = [];
  const seen = new Set();
  const walk = (id) => {
    const key = String(id);
    if (seen.has(key)) return; // cycle guard — see the header
    const node = index.nodeById.get(key);
    if (!node) return;
    seen.add(key);
    out.push(node);
    for (const child of index.childrenByParent.get(key) || []) walk(child.id);
  };
  walk(rootId);
  return out;
}

/**
 * Is `candidateId` somewhere beneath `ofId`? Walks UP from the candidate, so it
 * costs the depth rather than the subtree — the shape the sidebar's `isAncestor`
 * already had. A node is not its own descendant; callers that must also refuse
 * "into itself" check that separately, because the two are different refusals
 * with different messages.
 */
export function isDescendant(nodesOrIndex, candidateId, ofId) {
  const { nodeById } = asIndex(nodesOrIndex);
  const target = String(ofId);
  const seen = new Set([String(candidateId)]);
  let cur = nodeById.get(String(candidateId));
  while (cur) {
    const parentId = cur.parentId == null ? null : String(cur.parentId);
    if (parentId === null) return false;
    if (parentId === target) return true;
    if (seen.has(parentId)) return false; // cycle guard
    seen.add(parentId);
    cur = nodeById.get(parentId);
  }
  return false;
}
